import React, { useState } from 'react';
import {
  CardHeader,
  CardHeaderProps,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useNotify } from 'react-admin';
import { downloadExcel, ExportColumn } from '../utils/exportExcel';
import { DashboardExportParams, fetchDashboardExport } from '../utils/dashboardExportApi';

type DashboardPanelHeaderProps = CardHeaderProps & {
  exportFilename: string;
  exportPanel?: string;
  exportParams?: Omit<DashboardExportParams, 'panel'>;
  summaryRows?: Record<string, unknown>[];
  summaryColumns?: ExportColumn[];
  summarySheetName?: string;
};

const DashboardPanelHeader: React.FC<DashboardPanelHeaderProps> = ({
  exportFilename,
  exportPanel,
  exportParams,
  summaryRows,
  summaryColumns,
  summarySheetName = 'Summary',
  action,
  ...headerProps
}) => {
  const notify = useNotify();
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      if (exportPanel) {
        const result = await fetchDashboardExport({
          panel: exportPanel,
          ...exportParams,
        });
        if (!result.rows?.length) {
          notify('No rows to export for the selected filters', { type: 'info' });
          return;
        }
        downloadExcel({
          filename: exportFilename,
          sheets: [{ name: 'Data', rows: result.rows }],
        });
        notify(`Exported ${result.rows.length.toLocaleString()} row(s)`, { type: 'success' });
        return;
      }

      if (summaryRows?.length) {
        downloadExcel({
          filename: exportFilename,
          sheets: [
            {
              name: summarySheetName,
              rows: summaryRows,
              columns: summaryColumns,
            },
          ],
        });
        notify(`Exported ${summaryRows.length.toLocaleString()} row(s)`, { type: 'success' });
        return;
      }

      notify('Nothing to export for this panel', { type: 'warning' });
    } catch (error: any) {
      console.error(error);
      notify(error?.response?.data?.message || 'Export failed', { type: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  const downloadAction = (
    <Tooltip title="Download row-level Excel (up to 25,000 rows)">
      <span>
        <IconButton
          size="small"
          onClick={handleDownload}
          disabled={downloading}
          aria-label="Download Excel"
        >
          {downloading ? <CircularProgress size={18} /> : <FileDownloadIcon fontSize="small" />}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <CardHeader
      {...headerProps}
      action={
        <>
          {action}
          {downloadAction}
        </>
      }
    />
  );
};

export default DashboardPanelHeader;
